import {
  ScaffolderInputHeader,
  ScaffolderFormPaper,
  ScaffolderFormHeader,
  ScaffolderFieldError,
} from '@internal/plugin-scaffolder-common-field-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import useAsync from 'react-use/lib/useAsync';

import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import {
  Box,
  CircularProgress,
  TextField,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { AzureSecurityGroupMultiValue } from '../../AzureSecurityGroupMultiValue';
import { CicdOidcSubjectsInput } from '../components/CicdOidcSubjectsInput';
import { OWNER_INFO, SECURITY_GROUPS_INFO } from '../infoDoc';
import { useStyles } from '../styles';
import { ProjectOwnerValue } from '../types';
import {
  cicdOidcSubjectsInputErrors,
  ownerInputErrors,
  securityGroupsInputErrors,
} from '../validation';

interface OwnerSectionProps {
  value: ProjectOwnerValue;
  onChange: (value: ProjectOwnerValue) => void;
  errors?: string[];
}

const OwnerSectionInner = ({ value, onChange, errors }: OwnerSectionProps) => {
  const catalogApi = useApi(catalogApiRef);
  const classes = useStyles();

  // Stable refs — keeps useCallback deps empty ([]) without stale closures.
  // Always read .current inside callbacks instead of closing over the prop directly.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;

  const ownerError =
    errors && errors.length > 0 ? ownerInputErrors(value.owner) : '';
  const hasOwnerError = ownerError !== '';

  const securityGroupsError =
    errors && errors.length > 0
      ? securityGroupsInputErrors(value.securityGroups)
      : '';

  const cicdSubjectsError =
    errors && errors.length > 0
      ? cicdOidcSubjectsInputErrors(value.cicdOidcSubjects)
      : '';

  const ownerRef = useRef<HTMLDivElement>(null);
  const securityGroupsRef = useRef<HTMLDivElement>(null);
  const cicdSubjectsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasOwnerError) {
      ownerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (securityGroupsError) {
      securityGroupsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    } else if (cicdSubjectsError) {
      cicdSubjectsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [hasOwnerError, securityGroupsError, cicdSubjectsError]);

  const { value: ownerEntities, loading: ownersLoading } = useAsync(() =>
    catalogApi.getEntities({
      filter: { kind: 'Group', 'spec.type': 'supervisory-org' },
    }),
  );
  const ownerOptions: Entity[] = useMemo(
    () => ownerEntities?.items || [],
    [ownerEntities],
  );
  const selectedOwner = useMemo(
    () => ownerOptions.find(e => stringifyEntityRef(e) === value.owner) || null,
    [ownerOptions, value.owner],
  );

  const handleOwnerChange = useCallback((_: any, entity: Entity | null) => {
    onChangeRef.current({
      ...valueRef.current,
      owner: entity ? stringifyEntityRef(entity) : '',
    });
  }, []);

  const handleGroupsChange = useCallback(
    (_: any, selected: string[] | undefined) => {
      onChangeRef.current({
        ...valueRef.current,
        securityGroups: selected || [],
      });
    },
    [],
  );

  const handleCicdSubjectsChange = useCallback((subjects: string[]) => {
    onChangeRef.current({
      ...valueRef.current,
      cicdOidcSubjects: subjects,
    });
  }, []);

  return (
    <Box>
      <ScaffolderFormPaper>
        <ScaffolderFormHeader
          title="Ownership and access control"
          description="Configure the ownership details for the Deployment as a Service Project"
          divider
        />
        <div ref={ownerRef} className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Project owner"
            description="Select the owner of this project. This should be a supervisory org group in the catalog."
            infoDoc={OWNER_INFO}
          />
          <Autocomplete
            size="small"
            value={selectedOwner}
            loading={ownersLoading}
            onChange={handleOwnerChange}
            options={ownerOptions}
            getOptionLabel={option =>
              (option.metadata.title || option.metadata.name) as string
            }
            renderInput={params => (
              <TextField
                {...params}
                label="Owner"
                variant="outlined"
                color="secondary"
                required
                size="small"
                error={hasOwnerError}
                helperText={
                  hasOwnerError ? (
                    <ScaffolderFieldError message={ownerError} />
                  ) : undefined
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {ownersLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={option => (
              <Box>
                <Typography variant="body2" noWrap>
                  {option.metadata.title || option.metadata.name}
                </Typography>
                <Typography variant="caption" noWrap color="textSecondary">
                  {stringifyEntityRef(option)}
                </Typography>
              </Box>
            )}
            noOptionsText="No owners found"
            loadingText="Loading owners..."
          />
        </div>
        <div ref={securityGroupsRef} className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Project security groups"
            description="Select security groups that allows members to access, edit or delete this project. Your own groups are listed by default; start typing a group name to search all Azure security groups."
            infoDoc={SECURITY_GROUPS_INFO}
          />
          <AzureSecurityGroupMultiValue
            name="Security Groups"
            schema={{
              title: 'Security Groups',
            }}
            formData={value.securityGroups || []}
            onChange={selected => handleGroupsChange(null, selected)}
            required
            idSchema={[] as any}
            uiSchema={{
              'ui:options': {
                excludeWellKnownGroups: true,
              },
            }}
            onBlur={() => {}}
            onFocus={() => {}}
            readonly={false}
            disabled={false}
            registry={{} as any}
            rawErrors={securityGroupsError ? [securityGroupsError] : []}
          />
        </div>
        <div ref={cicdSubjectsRef}>
          <CicdOidcSubjectsInput
            formData={value.cicdOidcSubjects || []}
            onChange={handleCicdSubjectsChange}
            rawErrors={cicdSubjectsError ? [cicdSubjectsError] : []}
          />
          {cicdSubjectsError && (
            <Box className={classes.inputContainer}>
              <ScaffolderFieldError message={cicdSubjectsError} />
            </Box>
          )}
        </div>
      </ScaffolderFormPaper>
    </Box>
  );
};

export const OwnerSection = React.memo(OwnerSectionInner);
